if (!customElements.get('matching-set')) {
  customElements.define(
    'matching-set',
    class MatchingSet extends HTMLElement {
      constructor() {
        super();

        this.selected = false;
        this.currentMainVariant = null;
        this.currentConfiguration = null;
        this.onVariantChangeUnsubscriber = null;
        this.onChoiceChange = this.onChoiceChange.bind(this);
        this.onSetCardClick = this.onSetCardClick.bind(this);
        this.onModalAdd = this.onModalAdd.bind(this);
        this.onThumbnailClick = this.onThumbnailClick.bind(this);
        this.onPreviousImage = this.onPreviousImage.bind(this);
        this.onNextImage = this.onNextImage.bind(this);
      }

      connectedCallback() {
        this.choiceInputs = Array.from(this.querySelectorAll('[data-matching-set-choice]'));
        this.choiceCards = Array.from(this.querySelectorAll('[data-matching-set-choice-card]'));
        this.configurationNodes = Array.from(this.querySelectorAll('[data-matching-set-configuration]'));
        this.singlePrice = this.querySelector('[data-matching-set-single-price]');
        this.setPrice = this.querySelector('[data-matching-set-set-price]');
        this.comparePrice = this.querySelector('[data-matching-set-compare-price]');
        this.setPriceGroup = this.querySelector('[data-matching-set-price-group]');
        this.availability = this.querySelector('[data-matching-set-availability]');
        this.setCard = this.querySelector('[data-matching-set-choice-card="set"]');
        this.productInfo = this.closest('product-info');
        this.productForm = this.productInfo?.querySelector('product-form');
        this.quickViewModal = document.getElementById(this.dataset.modalId);
        this.quickImage = this.quickViewModal?.querySelector('[data-matching-set-quick-image]');
        this.thumbnailButtons = Array.from(
          this.quickViewModal?.querySelectorAll('[data-matching-set-thumbnail]') || []
        );
        this.previousImageButton = this.quickViewModal?.querySelector('[data-matching-set-previous-image]');
        this.nextImageButton = this.quickViewModal?.querySelector('[data-matching-set-next-image]');
        this.modalAddButton = this.quickViewModal?.querySelector('[data-matching-set-modal-add]');

        if (this.choiceInputs.length !== 2 || !this.productForm) return;

        this.choiceInputs.forEach((input) => input.addEventListener('change', this.onChoiceChange));
        this.setCard?.addEventListener('click', this.onSetCardClick);
        this.modalAddButton?.addEventListener('click', this.onModalAdd);
        this.thumbnailButtons.forEach((button) => button.addEventListener('click', this.onThumbnailClick));
        this.previousImageButton?.addEventListener('click', this.onPreviousImage);
        this.nextImageButton?.addEventListener('click', this.onNextImage);
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );

        const selectedVariant = this.closest('product-info')
          ?.querySelector('variant-selects [data-selected-variant]')
          ?.textContent.trim();

        if (selectedVariant) {
          try {
            this.updateVariant(JSON.parse(selectedVariant));
          } catch (error) {
            console.error('Unable to initialize matching set variant', error);
          }
        } else if (this.dataset.initialMainVariantId) {
          this.updateVariant({ id: this.dataset.initialMainVariantId, available: true });
        }
      }

      disconnectedCallback() {
        this.choiceInputs?.forEach((input) => input.removeEventListener('change', this.onChoiceChange));
        this.setCard?.removeEventListener('click', this.onSetCardClick);
        this.modalAddButton?.removeEventListener('click', this.onModalAdd);
        this.thumbnailButtons?.forEach((button) => button.removeEventListener('click', this.onThumbnailClick));
        this.previousImageButton?.removeEventListener('click', this.onPreviousImage);
        this.nextImageButton?.removeEventListener('click', this.onNextImage);
        this.onVariantChangeUnsubscriber?.();
      }

      handleVariantChange({ data: { sectionId, variant } }) {
        if (String(sectionId) !== this.dataset.sectionId) return;
        this.updateVariant(variant);
      }

      updateVariant(mainVariant) {
        this.currentMainVariant = mainVariant;
        this.currentConfiguration =
          this.configurationNodes.find(
            (configuration) => configuration.dataset.mainVariantId === String(mainVariant?.id)
          ) || null;

        if (this.selected && !this.matchingVariantAvailable) {
          this.selected = false;
        }

        this.render();
      }

      render() {
        const configuration = this.currentConfiguration;
        const hasMatchingVariant = Boolean(configuration?.dataset.matchingVariantId);
        const setInput = this.choiceInputs.find((input) => input.value === 'set');
        const singleInput = this.choiceInputs.find((input) => input.value === 'single');

        if (!this.matchingVariantAvailable) this.selected = false;

        this.dataset.selected = String(this.selected);
        this.dataset.matchingVariantId = configuration?.dataset.matchingVariantId || '';

        if (singleInput) singleInput.checked = !this.selected;
        if (setInput) {
          setInput.checked = this.selected;
          setInput.disabled = !this.matchingVariantAvailable;
        }

        this.choiceCards.forEach((card) => {
          const isSetCard = card.dataset.matchingSetChoiceCard === 'set';
          const isSelected = isSetCard ? this.selected : !this.selected;
          card.classList.toggle('is-selected', isSelected);

          if (isSetCard) {
            card.setAttribute('aria-disabled', String(!this.matchingVariantAvailable));
          }
        });

        this.updateText(this.singlePrice, configuration?.dataset.mainPrice);
        this.updateText(this.setPrice, configuration?.dataset.setPriceTen);
        this.updateText(this.comparePrice, configuration?.dataset.regularPrice);

        this.updateImage(
          this.quickImage,
          configuration?.dataset.matchingQuickImageSrc,
          configuration?.dataset.matchingImageAlt
        );
        this.updateActiveThumbnail(configuration?.dataset.matchingImageId);

        if (this.setPriceGroup) this.setPriceGroup.hidden = !this.matchingVariantAvailable;
        if (this.availability) {
          this.availability.hidden = this.matchingVariantAvailable;
          this.availability.textContent = hasMatchingVariant
            ? this.dataset.soldOutLabel
            : this.dataset.unavailableLabel;
        }
        if (this.modalAddButton) {
          this.modalAddButton.disabled = !this.matchingVariantAvailable;
          this.modalAddButton.textContent = this.matchingVariantAvailable
            ? 'Set selecteren'
            : hasMatchingVariant
              ? this.dataset.soldOutLabel
              : this.dataset.unavailableLabel;
        }
      }

      onChoiceChange(event) {
        if (event.target.value === 'set' && !this.matchingVariantAvailable) return;
        this.selected = event.target.value === 'set';
        this.render();
      }

      onSetCardClick(event) {
        if (event.target.closest('button, input, a, modal-opener') || !this.matchingVariantAvailable) return;

        this.selected = true;
        this.render();
      }

      onModalAdd() {
        if (!this.matchingVariantAvailable) return;

        this.selected = true;
        this.render();
        this.quickViewModal?.hide();
      }

      onThumbnailClick(event) {
        const button = event.currentTarget;
        this.showThumbnail(button);
      }

      onPreviousImage() {
        this.showAdjacentThumbnail(-1);
      }

      onNextImage() {
        this.showAdjacentThumbnail(1);
      }

      showAdjacentThumbnail(direction) {
        if (!this.thumbnailButtons?.length) return;

        const currentIndex = this.thumbnailButtons.findIndex((button) => button.classList.contains('is-active'));
        const startingIndex = currentIndex >= 0 ? currentIndex : direction > 0 ? -1 : 0;
        const nextIndex = (startingIndex + direction + this.thumbnailButtons.length) % this.thumbnailButtons.length;
        this.showThumbnail(this.thumbnailButtons[nextIndex]);
      }

      showThumbnail(button) {
        this.updateImage(this.quickImage, button.dataset.imageSrc, button.dataset.imageAlt);
        this.updateActiveThumbnail(button.dataset.imageId);
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      updateText(element, value = '') {
        if (element) element.textContent = value || '';
      }

      updateImage(image, source = '', alt = '') {
        if (!image) return;

        if (source) {
          image.src = source;
          image.alt = alt || '';
          image.hidden = false;
        } else {
          image.removeAttribute('src');
          image.alt = '';
          image.hidden = true;
        }
      }

      updateActiveThumbnail(imageId = '') {
        this.thumbnailButtons?.forEach((button) => {
          const active = Boolean(imageId) && button.dataset.imageId === String(imageId);
          button.classList.toggle('is-active', active);
          button.toggleAttribute('aria-current', active);
        });
      }

      get matchingVariantAvailable() {
        return this.currentConfiguration?.dataset.matchingAvailable === 'true';
      }

      get isSelected() {
        return this.selected && this.matchingVariantAvailable;
      }

      get selectedVariantId() {
        return this.isSelected ? this.currentConfiguration.dataset.matchingVariantId : null;
      }
    }
  );
}
